import React, { useState, useEffect, useCallback } from 'react';
import { Table, Row, Col, Button, Typography, Divider, Pagination as Paging } from 'antd';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { GetInboxsetOverviewTableColumns } from './InboxsetOverviewTable';
import { PageHeader } from '../../../components/layout/PageHeader';
import { InboxFileRow, Project, Pagination, InboxFilters } from '../../../types';
import { Dataset } from '../../../types/dataset';
import { getProjects } from '../../../lib/api/aws-api';
import { updateInboxFileStatus, deleteInboxFile, updateClientInboxFilesStatus } from '../../../lib/api/inbox-api';
import { getDataset } from '../../../lib/api/dataset-api';
import { ExportStagedDocuments } from './ExportStagedDocuments';
import { Icon } from '@ant-design/compatible';
import { useGetClientAndInboxes, useGetFlows, useGetInboxFileList } from '../../../lib/hooks';
import { renderFilterString, setFiltersOnMount, initialFilterValues } from '../Filters';
import { ErrorText } from '../../../components/text/ErrorText';

const { Text } = Typography;

const defaultPaging = {
  pageSize: 10,
  page: 0,
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const InboxsetOverview = () => {
  const history = useHistory();
  const location = useLocation();
  const query = useQuery();

  const { doctype, clientName } = useParams<{ doctype: string; clientName: string }>();

  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [linkedProject, setLinkedProject] = useState<Project>();
  const [linkedDataset, setLinkedDataset] = useState<Dataset>();
  const [queryString, setQueryString] = useState<string>('');
  const [filters, setFilters] = useState<InboxFilters>(initialFilterValues);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [exportingStaged, setExportingStaged] = useState<boolean>(false);
  const { flowOptions } = useGetFlows(clientName);

  const { client, inbox, refetch } = useGetClientAndInboxes(clientName, doctype);

  const { inboxFiles, filterItemsCount, filesLoading, refetchFiles, filesError } = useGetInboxFileList(
    client?.io_customer_name || '',
    paginationObject,
    doctype,
    queryString
  );

  useEffect(() => {
    const fetchDataset = async () => {
      if (inbox?.dataset_id) {
        const linkedDataset = await getDataset(inbox.dataset_id);
        setLinkedDataset(linkedDataset);
        const projects = await getProjects();
        const linkedProject = projects.find((project: Project) => project.id === linkedDataset.project_id);
        setLinkedProject(linkedProject);
      }
    };
    fetchDataset();
  }, [inbox]);

  //FILTER & SORT

  //ONMOUNT: get the query parameters and set filters on these values
  useEffect(() => {
    setFiltersOnMount(query, setFilters);

    if (query.get('page')) {
      setPaginationObject({
        pageSize: defaultPaging.pageSize,
        page: query.get('page') ? parseInt(query.get('page')!) : defaultPaging.page,
      });
    }
    //we dont want query in dependency arry => infinite loop:
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (client) {
      history.replace({
        pathname: location.pathname,
        search: 'page=' + paginationObject.page + '&size=' + paginationObject.pageSize + queryString,
      });
      refetchFiles(false);
    }
  }, [client, history, location.pathname, paginationObject, queryString, refetchFiles]);

  const checkPagesAfterFilter = useCallback(() => {
    const pageMinCapacity = paginationObject.page * paginationObject.pageSize;
    if (filterItemsCount > 0 && filterItemsCount < pageMinCapacity) {
      setPaginationObject(prev => ({ ...prev, page: 0 }));
    }
  }, [filterItemsCount, paginationObject]);

  //set filterquerystring whenever filter updates
  useEffect(() => {
    setQueryString(renderFilterString(filters));
    checkPagesAfterFilter();
  }, [filters, checkPagesAfterFilter]);

  // OTHER FUNCTIONS

  const handleTableChange = (x: any, y: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      setFilters(prev => ({
        ...prev,
        sort: sorter.map(s => ({ field: s.field, order: s.order })),
      }));
    } else {
      if (sorter.column === undefined) {
        setFilters(prev => ({ ...prev, sort: [{ field: 'time_created', order: 'descend' }] }));
        return;
      }
      setFilters(prev => ({
        ...prev,
        sort: [{ field: sorter.field, order: sorter.order }],
      }));
    }
  };

  const noFiltersActivated = () => {
    return !filters.tag && !filters.date.length && !filters.flow.length && !filters.score.length && !filters.status;
  };

  const toggleFileStatus = async (file: InboxFileRow) => {
    const newStatus = file.inbox_status === 'NEW' ? 'STAGED' : 'NEW';
    await updateInboxFileStatus(file.inbox_id, newStatus);
    refetchFiles(false);
    refetch();
  };

  const toggleAllFiles = async (status: string) => {
    if (noFiltersActivated()) {
      return false;
    }
    const alteredQueryString = queryString.slice(1).replace(/&sort.*/, '');
    await updateClientInboxFilesStatus(client!.io_customer_name, doctype, alteredQueryString, status);
    refetchFiles(true);
    refetch();
  };

  const onClickUrl = useCallback(
    (record: InboxFileRow) => {
      return `/inbox/${clientName}/${doctype}/file/${record.inbox_id}?page=${paginationObject.page}${queryString}`;
    },
    [clientName, doctype, queryString, paginationObject]
  );

  const onDelete = async (record: InboxFileRow) => {
    await deleteInboxFile(record.inbox_id);
    refetchFiles(true);
  };

  return (
    <>
      {client && inbox && (
        <ExportStagedDocuments
          title="Export Staged Documents"
          visible={modalVisible}
          onClose={() => {
            setModalVisible(false);
          }}
          onSave={() => {
            setModalVisible(false);
            refetch();
            refetchFiles(true);
          }}
          client={client!}
          inbox={inbox!}
          doctype={doctype}
          setExportingStaged={setExportingStaged}
          exportingStaged={exportingStaged}
        />
      )}
      <PageHeader
        back={{ url: `/inbox/${clientName}/`, label: ((client && client.io_customer_name) || '') + ' overview' }}
        header={`${client ? client.io_customer_name : ''} - ${inbox ? inbox.document_class : ''}`}
        description="Click on a file to see the individual field results."
      />
      <Row>
        {linkedDataset && linkedProject && (
          <Text>
            Linked Dataset: {linkedProject.name} - {linkedDataset.name}
          </Text>
        )}
      </Row>
      <Row className="items-center my-3">
        <Col span={12}>
          {!noFiltersActivated() ? (
            <>
              <Text>
                Filtered {filterItemsCount} of {inbox?.nrofdocumentsforclass} Results
              </Text>
              <Divider type="vertical" orientation="center" className="mx-3" />
              <Button onClick={() => setFilters(initialFilterValues)} type="link" className="pl-0">
                Reset all filters
                <Icon type="close" />
              </Button>
            </>
          ) : (
            <Text>{inbox?.nrofdocumentsforclass} Results</Text>
          )}
        </Col>
        <Col span={12} className="flex justify-end">
          {!noFiltersActivated() && (
            <>
              <Button onClick={() => toggleAllFiles('STAGED')} type="primary" size="large" className="mr-3">
                Set to Staged
              </Button>
              <Button onClick={() => toggleAllFiles('NEW')} type="primary" size="large" className="mr-5">
                Set to New
              </Button>
              <Divider type="vertical" orientation="center" style={{ height: '40px' }} />
            </>
          )}
          <Button
            disabled={false}
            onClick={() => setModalVisible(true)}
            size="large"
            className="bg-toolbox-secondary border-toolbox-secondary ml-5 text-white">
            Export Staged
          </Button>
        </Col>
      </Row>
      <Row>
        {filters && (
          <Col span={24}>
            {filesError ? (
              <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
            ) : (
              <>
                <Table
                  className="ant-table-toolbox"
                  columns={GetInboxsetOverviewTableColumns(
                    onClickUrl,
                    onDelete,
                    toggleFileStatus,
                    filters,
                    setFilters,
                    flowOptions
                  )}
                  rowKey={record => record.inbox_id}
                  dataSource={inboxFiles}
                  loading={filesLoading || exportingStaged}
                  pagination={false}
                  onChange={handleTableChange as any}
                />
                <div className="flex justify-end my-4">
                  {filterItemsCount && (
                    <Paging
                      showSizeChanger={false}
                      current={paginationObject.page + 1}
                      pageSize={defaultPaging.pageSize}
                      total={filterItemsCount}
                      onChange={page => {
                        setPaginationObject({ ...defaultPaging, page: page - 1 });
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </Col>
        )}
      </Row>
    </>
  );
};

export default InboxsetOverview;
